import { format } from "date-fns";
import { numberFormat } from "../../shared/CommonFuctions";

export type LedgerRowType = "OPENING" | "TXN" | "TOTAL" | "CLOSING";

export interface StockLedgerDetailRow {
  srNo: number;
  rowType: LedgerRowType;
  voucherDate: string | Date | null;
  voucherNo: string;
  voucherType: string;
  sourceType: string;
  voucherId: number;
  partyName: string;
  warehouseName: string;
  warehouseId: number;
  categoryId: number;
  inwardQty: number;
  outwardQty: number;
  rate: number;
  closingBalance: number;
  stockValue: number;
  nature: string;
  productId: number;
}

export type LedgerBuildOptions = {
  warehouseIds?: string[];
  categoryIds?: string[];
  voucherTypes?: string[];
  valuationMethod?: string;
};

export interface StockLedgerProductGroup {
  productId: number;
  productName: string;
  categoryName: string;
  details: StockLedgerDetailRow[];
  openingQty: number;
  openingValue: number;
  totalInward: number;
  totalOutward: number;
  closingQty: number;
  closingValue: number;
  isBalanced: boolean;
}

export const VOUCHER_TYPE_OPTIONS = [
  { label: "Purchase", value: "PURCHASE" },
  { label: "Sale", value: "SALE" },
  { label: "Purchase Return", value: "DEBITNOTE" },
  { label: "Sales Return", value: "CREDITNOTE" },
  { label: "Stock Transfer", value: "STOCKTRANSFER" },
  { label: "Adjustment", value: "STOCKADJUSTMENT" },
  { label: "Production", value: "PRODUCTION" },
];

export const VALUATION_OPTIONS = [
  { label: "Weighted Average", value: "WEIGHTED_AVERAGE" },
  { label: "FIFO", value: "FIFO" },
  { label: "Last Purchase Rate", value: "LAST_PURCHASE_RATE" },
];

export function formatLedgerDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() <= 1901) return "";
    return format(d, "dd-MM-yyyy");
  } catch {
    return "";
  }
}

export function qtyFmt(n: number | null | undefined): string {
  return numberFormat(Number(n || 0), 3, 3);
}

export function amtFmt(n: number | null | undefined): string {
  return numberFormat(Number(n || 0), 2, 2);
}

function normalizeMode(value: string): string {
  return String(value || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");
}

/** Map API item (Pascal/camel/snake) into a detail row */
export function mapApiRow(item: any): StockLedgerDetailRow {
  const closing =
    Number(
      item.ClosingBalance ?? item.closingBalance ?? item.closing_balance ?? 0
    ) || 0;
  const inward = Number(item.InwardQty ?? item.inwardQty ?? item.inward_qty ?? 0);
  const outward = Number(
    item.OutwardQty ?? item.outwardQty ?? item.outward_qty ?? 0
  );
  const stockValue = Number(
    item.StockValue ??
      item.stockValue ??
      item.stock_value ??
      item.PurchaseAmount ??
      item.purchaseAmount ??
      item.purchase_amount ??
      0
  );
  let rate = Number(item.Rate ?? item.rate ?? 0);
  const qtyForRate = inward > 0 ? inward : outward;
  if (!rate && qtyForRate) {
    rate = Math.abs(stockValue / qtyForRate);
  }

  const rowTypeRaw = String(
    item.RowType ?? item.rowType ?? item.row_type ?? ""
  ).toUpperCase();
  const sourceType = String(
    item.SourceType ?? item.sourceType ?? item.source_type ?? ""
  );
  const voucherType = String(
    item.VoucherType ?? item.voucherType ?? item.voucher_type ?? ""
  );
  let rowType: LedgerRowType = "TXN";
  if (rowTypeRaw === "OPENING" || rowTypeRaw === "TOTAL" || rowTypeRaw === "CLOSING") {
    rowType = rowTypeRaw;
  } else if (
    normalizeMode(sourceType) === "OPENING" ||
    normalizeMode(voucherType) === "OPENING"
  ) {
    rowType = "OPENING";
  }

  return {
    srNo: Number(item.SrNo ?? item.srNo ?? item.sr_no ?? 0),
    rowType,
    voucherDate: item.VoucherDate ?? item.voucherDate ?? item.voucher_date ?? null,
    voucherNo: item.VoucherNo ?? item.voucherNo ?? item.voucher_no ?? "",
    voucherType,
    sourceType,
    voucherId: Number(item.VoucherId ?? item.voucherId ?? item.voucher_id ?? 0),
    partyName: item.PartyName ?? item.partyName ?? item.party_name ?? "",
    warehouseName:
      item.WarehouseName ?? item.warehouseName ?? item.warehouse_name ?? "",
    warehouseId: Number(
      item.WarehouseId ?? item.warehouseId ?? item.warehouse_id ?? 0
    ),
    categoryId: Number(
      item.CategoryId ?? item.categoryId ?? item.category_id ?? 0
    ),
    inwardQty: inward,
    outwardQty: outward,
    rate,
    closingBalance: closing,
    stockValue,
    nature: item.Nature ?? item.nature ?? "",
    productId: Number(item.ProductId ?? item.productId ?? item.product_id ?? 0),
  };
}

function emptyLedger(productId: number): StockLedgerDetailRow[] {
  return [
    {
      srNo: 0,
      rowType: "OPENING",
      voucherDate: null,
      voucherNo: "",
      voucherType: "OPENING",
      sourceType: "OPENING",
      voucherId: 0,
      partyName: "Opening Balance",
      warehouseName: "",
      warehouseId: 0,
      categoryId: 0,
      inwardQty: 0,
      outwardQty: 0,
      rate: 0,
      closingBalance: 0,
      stockValue: 0,
      nature: "CR",
      productId,
    },
    {
      srNo: 0,
      rowType: "TOTAL",
      voucherDate: null,
      voucherNo: "",
      voucherType: "TOTAL",
      sourceType: "TOTAL",
      voucherId: 0,
      partyName: "Total",
      warehouseName: "",
      warehouseId: 0,
      categoryId: 0,
      inwardQty: 0,
      outwardQty: 0,
      rate: 0,
      closingBalance: 0,
      stockValue: 0,
      nature: "",
      productId,
    },
    {
      srNo: 0,
      rowType: "CLOSING",
      voucherDate: null,
      voucherNo: "",
      voucherType: "CLOSING",
      sourceType: "CLOSING",
      voucherId: 0,
      partyName: "Closing Balance",
      warehouseName: "",
      warehouseId: 0,
      categoryId: 0,
      inwardQty: 0,
      outwardQty: 0,
      rate: 0,
      closingBalance: 0,
      stockValue: 0,
      nature: "",
      productId,
    },
  ];
}

/**
 * Group flat API rows into product ledgers.
 * Always rebuilds Opening/Total/Closing with the selected valuation so filters
 * work even when the deployed SP ignores the new parameters.
 */
export function buildProductGroups(
  rawRows: any[],
  selectedProducts: { productId: number; name: string; categoryId?: number }[],
  options: LedgerBuildOptions = {}
): StockLedgerProductGroup[] {
  const warehouseIds = (options.warehouseIds || []).map(String).filter(Boolean);
  const categoryIds = (options.categoryIds || []).map(String).filter(Boolean);
  const voucherTypes = (options.voucherTypes || [])
    .map(normalizeMode)
    .filter(Boolean);
  const valuationMethod = options.valuationMethod || "WEIGHTED_AVERAGE";

  const mapped = (rawRows || []).map(mapApiRow);
  const byProduct = new Map<number, StockLedgerDetailRow[]>();

  mapped.forEach((row) => {
    if (!row.productId) return;
    if (!byProduct.has(row.productId)) byProduct.set(row.productId, []);
    byProduct.get(row.productId)!.push(row);
  });

  const nameById = new Map<number, string>();
  (rawRows || []).forEach((item: any) => {
    const id = Number(item.ProductId ?? item.productId ?? item.product_id ?? 0);
    const name = item.ProductName ?? item.productName ?? item.product_name;
    if (id && name) nameById.set(id, name);
  });

  let ensureSelected = selectedProducts.length
    ? selectedProducts
    : Array.from(byProduct.keys()).map((id) => ({
        productId: id,
        name: nameById.get(id) || String(id),
        categoryId: 0,
      }));

  // Category filter — product master category
  if (categoryIds.length) {
    ensureSelected = ensureSelected.filter((p) =>
      categoryIds.includes(String(p.categoryId || 0))
    );
  }

  const groups: StockLedgerProductGroup[] = [];

  ensureSelected.forEach((p) => {
    const productId = Number(p.productId);
    const productName = nameById.get(productId) || p.name || "Unknown";
    const allRows = [...(byProduct.get(productId) || [])];

    // Drop SP summary rows; rebuild from movements so valuation/filters apply
    let movements = allRows.filter(
      (d) => d.rowType !== "TOTAL" && d.rowType !== "CLOSING"
    );

    // Godown filter (keep OPENING so opening balance remains; filter period txns)
    if (warehouseIds.length) {
      movements = movements.filter((d) => {
        const isOpening =
          d.rowType === "OPENING" ||
          normalizeMode(d.sourceType) === "OPENING" ||
          normalizeMode(d.voucherType) === "OPENING";
        if (isOpening) return true;
        return warehouseIds.includes(String(d.warehouseId || 0));
      });
    }

    // Voucher type filter (keep OPENING)
    if (voucherTypes.length) {
      movements = movements.filter((d) => {
        const isOpening =
          d.rowType === "OPENING" ||
          normalizeMode(d.sourceType) === "OPENING" ||
          normalizeMode(d.voucherType) === "OPENING";
        if (isOpening) return true;
        const mode = normalizeMode(d.sourceType || d.voucherType);
        return voucherTypes.some(
          (vt) =>
            mode === vt ||
            mode.includes(vt) ||
            vt.includes(mode) ||
            (vt === "CREDITNOTE" && mode.includes("SALESRETURN")) ||
            (vt === "DEBITNOTE" && mode.includes("PURCHASERETURN")) ||
            (vt === "STOCKADJUSTMENT" && mode.includes("ADJUSTMENT")) ||
            (vt === "STOCKTRANSFER" && mode.includes("TRANSFER"))
        );
      });
    }

    let details =
      movements.length > 0
        ? synthesizeLedgerRows(movements, valuationMethod)
        : emptyLedger(productId);

    const opening = details.find((d) => d.rowType === "OPENING");
    const total = details.find((d) => d.rowType === "TOTAL");
    const closing = details.find((d) => d.rowType === "CLOSING");
    const openingQty = opening?.closingBalance ?? opening?.inwardQty ?? 0;
    const totalInward = total?.inwardQty ?? 0;
    const totalOutward = total?.outwardQty ?? 0;
    const closingQty = closing?.closingBalance ?? 0;
    const expected =
      Number(openingQty) + Number(totalInward) - Number(totalOutward);
    const isBalanced = Math.abs(expected - Number(closingQty)) < 0.001;

    groups.push({
      productId,
      productName,
      categoryName: "",
      details,
      openingQty: Number(openingQty),
      openingValue: Number(opening?.stockValue || 0),
      totalInward: Number(totalInward),
      totalOutward: Number(totalOutward),
      closingQty: Number(closingQty),
      closingValue: Number(closing?.stockValue || 0),
      isBalanced,
    });
  });

  return groups;
}

/** Rebuild Opening / TXN / Total / Closing with selected valuation method */
function synthesizeLedgerRows(
  txns: StockLedgerDetailRow[],
  valuationMethod: string = "WEIGHTED_AVERAGE"
): StockLedgerDetailRow[] {
  const method = String(valuationMethod || "WEIGHTED_AVERAGE").toUpperCase();
  const productId = txns[0]?.productId || 0;
  const sorted = [...txns].sort((a, b) => {
    const da = new Date(a.voucherDate || 0).getTime();
    const db = new Date(b.voucherDate || 0).getTime();
    if (da !== db) return da - db;
    return String(a.voucherNo).localeCompare(String(b.voucherNo));
  });

  let balance = 0;
  let value = 0;
  let avg = 0;
  let lastPurchaseRate = 0;
  const fifoLayers: { qty: number; rate: number }[] = [];

  const openingTxns = sorted.filter(
    (t) =>
      t.rowType === "OPENING" ||
      normalizeMode(t.sourceType) === "OPENING" ||
      normalizeMode(t.voucherType) === "OPENING"
  );
  const periodTxns = sorted.filter(
    (t) =>
      t.rowType !== "OPENING" &&
      normalizeMode(t.sourceType) !== "OPENING" &&
      normalizeMode(t.voucherType) !== "OPENING" &&
      t.rowType !== "TOTAL" &&
      t.rowType !== "CLOSING"
  );

  openingTxns.forEach((t) => {
    const qty = Number(t.inwardQty) - Number(t.outwardQty);
    const lineValue =
      Number(t.stockValue) ||
      qty * Number(t.rate || 0) ||
      Number(t.closingBalance) * Number(t.rate || 0);
    balance += qty !== 0 ? qty : Number(t.closingBalance) || 0;
    value += lineValue;
  });
  if (!balance && openingTxns.length === 1) {
    balance = Number(openingTxns[0].closingBalance) || 0;
    value =
      Number(openingTxns[0].stockValue) ||
      balance * Number(openingTxns[0].rate || 0);
  }
  avg = balance !== 0 ? Math.abs(value / balance) : 0;
  lastPurchaseRate = avg;
  if (balance > 0 && method === "FIFO") {
    fifoLayers.push({ qty: balance, rate: avg || 0 });
  }

  const currentStockValue = () => {
    if (method === "LAST_PURCHASE_RATE") {
      return balance * (lastPurchaseRate || avg);
    }
    if (method === "FIFO") {
      return fifoLayers.reduce((s, l) => s + l.qty * l.rate, 0);
    }
    return balance * avg;
  };

  const rows: StockLedgerDetailRow[] = [
    {
      srNo: 0,
      rowType: "OPENING",
      voucherDate: null,
      voucherNo: "",
      voucherType: "OPENING",
      sourceType: "OPENING",
      voucherId: 0,
      partyName: "Opening Balance",
      warehouseName: "",
      warehouseId: 0,
      categoryId: 0,
      inwardQty: balance > 0 ? balance : 0,
      outwardQty: balance < 0 ? Math.abs(balance) : 0,
      rate: avg,
      closingBalance: balance,
      stockValue: currentStockValue(),
      nature: "CR",
      productId,
    },
  ];

  let tin = 0;
  let tout = 0;
  periodTxns.forEach((t) => {
    const inq = Number(t.inwardQty) || 0;
    const outq = Number(t.outwardQty) || 0;
    const rate = Number(t.rate) || avg || 0;
    tin += inq;
    tout += outq;

    if (inq > 0) {
      balance += inq;
      value += inq * rate;
      avg = balance !== 0 ? Math.abs(value / balance) : avg;
      if (
        ["PURCHASE", "OPENING", "PRODUCTION", "DEBITNOTE"].includes(
          normalizeMode(t.sourceType || t.voucherType)
        ) ||
        rate > 0
      ) {
        if (
          ["PURCHASE", "PRODUCTION"].includes(
            normalizeMode(t.sourceType || t.voucherType)
          )
        ) {
          lastPurchaseRate = rate;
        }
      }
      if (method === "FIFO") {
        fifoLayers.push({ qty: inq, rate });
      }
    } else if (outq > 0) {
      if (method === "FIFO") {
        let need = outq;
        let outVal = 0;
        while (need > 0 && fifoLayers.length) {
          const layer = fifoLayers[0];
          const take = Math.min(layer.qty, need);
          outVal += take * layer.rate;
          layer.qty -= take;
          need -= take;
          if (layer.qty <= 0.0000001) fifoLayers.shift();
        }
        if (need > 0) outVal += need * avg;
        value -= outVal;
      } else {
        value -= outq * avg;
      }
      balance -= outq;
      avg = balance !== 0 ? Math.abs(value / balance) : avg;
    }

    rows.push({
      ...t,
      rowType: "TXN",
      closingBalance: balance,
      stockValue: currentStockValue(),
      rate: rate,
    });
  });

  const stockVal = currentStockValue();
  rows.push({
    srNo: 0,
    rowType: "TOTAL",
    voucherDate: null,
    voucherNo: "",
    voucherType: "TOTAL",
    sourceType: "TOTAL",
    voucherId: 0,
    partyName: "Total",
    warehouseName: "",
    warehouseId: 0,
    categoryId: 0,
    inwardQty: tin,
    outwardQty: tout,
    rate: 0,
    closingBalance: balance,
    stockValue: stockVal,
    nature: "",
    productId,
  });
  rows.push({
    srNo: 0,
    rowType: "CLOSING",
    voucherDate: null,
    voucherNo: "",
    voucherType: "CLOSING",
    sourceType: "CLOSING",
    voucherId: 0,
    partyName: "Closing Balance",
    warehouseName: "",
    warehouseId: 0,
    categoryId: 0,
    inwardQty: 0,
    outwardQty: 0,
    rate:
      method === "LAST_PURCHASE_RATE" ? lastPurchaseRate || avg : avg,
    closingBalance: balance,
    stockValue: stockVal,
    nature: "",
    productId,
  });

  return rows;
}

export function exportLedgerToCsvOrExcel(
  groups: StockLedgerProductGroup[],
  meta: {
    companyName: string;
    periodFrom: string;
    periodTo: string;
    valuation: string;
    type: "csv" | "excel";
  }
) {
  const headers = [
    "Product",
    "Date",
    "Voucher No",
    "Voucher Type",
    "Party / Particulars",
    "Godown",
    "Inward Qty",
    "Outward Qty",
    "Rate (₹)",
    "Closing Balance (Qty)",
    "Stock Value (₹)",
  ];

  const lines: string[] = [];
  lines.push(`Company,${escapeCsv(meta.companyName)}`);
  lines.push(`Report,Stock Ledger Report`);
  lines.push(`Period,${meta.periodFrom} TO ${meta.periodTo}`);
  lines.push(`Valuation,${meta.valuation}`);
  lines.push("");
  lines.push(headers.join(","));

  groups.forEach((g) => {
    g.details.forEach((d) => {
      lines.push(
        [
          escapeCsv(g.productName),
          escapeCsv(
            d.rowType === "OPENING"
              ? "Opening Balance"
              : d.rowType === "TOTAL"
              ? "Total"
              : d.rowType === "CLOSING"
              ? "Closing Balance"
              : formatLedgerDate(d.voucherDate)
          ),
          escapeCsv(d.voucherNo),
          escapeCsv(d.voucherType),
          escapeCsv(d.partyName),
          escapeCsv(d.warehouseName),
          d.inwardQty,
          d.outwardQty,
          d.rate,
          d.closingBalance,
          d.stockValue,
        ].join(",")
      );
    });
    lines.push("");
  });

  const content = "\uFEFF" + lines.join("\r\n");
  const mime =
    meta.type === "csv"
      ? "text/csv;charset=utf-8;"
      : "application/vnd.ms-excel;charset=utf-8;";
  const ext = meta.type === "csv" ? "csv" : "xls";
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `StockLedgerReport_${format(new Date(), "yyyyMMdd")}.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeCsv(v: any): string {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function printLedger(
  groups: StockLedgerProductGroup[],
  meta: { companyName: string; periodFrom: string; periodTo: string; valuation: string }
) {
  const w = window.open("", "_blank", "width=1200,height=800");
  if (!w) return;

  const tables = groups
    .map((g) => {
      const rows = g.details
        .map((d) => {
          const isSummary = d.rowType !== "TXN";
          const dateLabel =
            d.rowType === "OPENING"
              ? "Opening Balance"
              : d.rowType === "TOTAL"
              ? "Total"
              : d.rowType === "CLOSING"
              ? "Closing Balance"
              : formatLedgerDate(d.voucherDate);
          const neg = Number(d.closingBalance) < 0 ? "color:#c62828;font-weight:600;" : "";
          return `<tr style="${isSummary ? "font-weight:700;background:#f5f7ff;" : ""}">
            <td>${dateLabel}</td>
            <td>${d.voucherNo || ""}</td>
            <td>${d.voucherType || ""}</td>
            <td>${d.partyName || ""}</td>
            <td>${d.warehouseName || ""}</td>
            <td style="text-align:right">${qtyFmt(d.inwardQty)}</td>
            <td style="text-align:right">${qtyFmt(d.outwardQty)}</td>
            <td style="text-align:right">${amtFmt(d.rate)}</td>
            <td style="text-align:right;${neg}">${qtyFmt(d.closingBalance)}</td>
            <td style="text-align:right">${amtFmt(d.stockValue)}</td>
          </tr>`;
        })
        .join("");
      return `<h3 style="margin:16px 0 8px;color:#25258f">${g.productName}</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th><th>Voucher No</th><th>Voucher Type</th>
              <th>Party / Particulars</th><th>Godown</th>
              <th>Inward Qty</th><th>Outward Qty</th><th>Rate (₹)</th>
              <th>Closing Balance (Qty)</th><th>Stock Value (₹)</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>`;
    })
    .join("");

  w.document.write(`<!DOCTYPE html><html><head><title>Stock Ledger Report</title>
    <style>
      body{font-family:Segoe UI,Arial,sans-serif;color:#222;padding:24px}
      h1{color:#25258f;margin:0 0 4px;font-size:20px}
      .meta{color:#555;font-size:12px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}
      th,td{border:1px solid #ddd;padding:6px 8px}
      th{background:#eef0fb;text-align:left}
      @media print{button{display:none}}
    </style></head><body>
    <h1>${meta.companyName || "Company"}</h1>
    <div class="meta">
      <div><strong>Stock Ledger Report</strong></div>
      <div>Period: ${meta.periodFrom} TO ${meta.periodTo}</div>
      <div>Valuation: ${meta.valuation}</div>
    </div>
    ${tables}
    <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}
